import styles from './index.module.css'

import * as React from 'react'

import { Trans } from '@disco/i18n/trans'
import { useTranslation } from '@disco/i18n/use-translation'

import NotFoundIllustration from 'assets/icons/not-found.svg'

// Interpolated rather than written into the translatable string, so a
// translator cannot corrupt the address the reader is told to write to.
const SUPPORT_EMAIL = 'support@disco.ac'

type Props = {
  title?: React.ReactNode
  subtitle?: React.ReactNode
  body?: React.ReactNode
}

// `common` rather than `shares`: this renders from three webpack entries. The
// in-app 404 and login shortlink (app), the playlist and track unavailable
// states (play), and the channel share page (channel). All three initialise
// i18next; see the import in each entry.
export default function NotFoundPage({
  title,
  subtitle,
  body,
}: Props): React.ReactElement {
  const { t } = useTranslation('common')

  // Checked against undefined rather than falling back with `??` so that a
  // caller passing null still renders nothing, as it did when these were
  // default parameters.
  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <NotFoundIllustration className={styles.illustration} />
        <div className={styles.textBlock}>
          <h1 className={styles.title}>
            {title === undefined
              ? t('common:oopsPageNotFound', 'Oops! Page not found')
              : title}
          </h1>
          <p className={styles.subtitle}>
            {subtitle === undefined
              ? t(
                  'common:weCantFindThePageYoureLookingFor',
                  "We can't find the page you're looking for.",
                )
              : subtitle}
          </p>
          <p className={styles.body}>
            {body === undefined ? (
              <Trans
                t={t}
                i18nKey="common:ifSomeoneSentYouALink"
                defaults="If someone sent you a playlist or track link, ask them to resend it - it might have moved. Still stuck? Hit up <support>{{email}}</support>."
                values={{ email: SUPPORT_EMAIL }}
                components={{
                  support: (
                    // Trans fills the children from the translated string, so
                    // the rule cannot see the content from here.
                    // eslint-disable-next-line jsx-a11y/anchor-has-content
                    <a
                      href={`mailto:${SUPPORT_EMAIL}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.link}
                    />
                  ),
                }}
              />
            ) : (
              body
            )}
          </p>
        </div>
      </div>
    </div>
  )
}
